import { FC, useEffect } from 'react';
import { IQueryParamDrivenDrawerContentProvider } from '../../components/query-param-driver-drawer/IQueryParamDrivenDrawerContentProvider';
import { QueryParamDrivenDrawerHelper } from '../../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { CaremapNotesModelFactory } from '../model/CaremapNotesModelFactory';
import { useSubscription } from '@meraki-internal/state';
import { QueryParamDrivenDrawerPage } from '../../components/CareMapDrawerPage';
import { BlockUntilActiveCaremap } from '../../ActiveCareMapProvider';
import { createUseStyles } from 'react-jss';
import { ViewNoteMenu } from './ViewNoteMenu';
import { NoteDateTarget } from '../note-list/CareMapNotesListTitle';
import { UserNotes } from './UserNotes';
import { EditNoteWithQueryParamDrawer } from '../edit-note/EditNoteWithQueryParamDrawer';
import { Overview } from './Overview';
import { SmartTasks } from './SmartTasks';
import { Attachments } from './Attachments';
import { DetailedSummary } from './DetailedSummary';
import { Transcription } from './Transcription';
import { Participants } from './Participants';
import { CareMapNoteTracker } from '../model/CareMapNoteTracker';
import { ShareNoteNudge } from './ShareNoteNudge';
import { BeaCommentBubble } from './BeaCommentBubble';
import { AppointmentLink } from './AppointmentLink';
import { AppointmentsState } from '../../appointments/AppointmentsState';
import { AppointmentEditorDrawer } from '../../appointments/AppointmentEditorDrawer';
import { TagsSection } from './TagsSection';
import { SummaryFeedback } from './SummaryFeedback';

const useStyles = createUseStyles({
    stretchHeight: {
        display: 'flex',
        flexDirection: 'column'
    }
});

interface IViewNoteDrawerProps {
    noteId: string;
    trackingFrom?: string;
};

export class ViewNoteWithQueryParamDrawer implements IQueryParamDrivenDrawerContentProvider<IViewNoteDrawerProps> {
    static inject = () => [QueryParamDrivenDrawerHelper];
    constructor(private drawerHelper: QueryParamDrivenDrawerHelper<IViewNoteDrawerProps>){}

    mode = 'view-note';

    Component = ViewNoteDrawerView;

    options = {
        allowBackdropClose: true
    };

    open = (props: IViewNoteDrawerProps) => {
        this.drawerHelper.open({ mode: this.mode, ...props });
    };

    getUrl = (props: IViewNoteDrawerProps) => {
        return this.drawerHelper.getUrl({ mode: this.mode, ...props });
    };
};

export const ViewNoteDrawerContent: FC<IViewNoteDrawerProps> = ({ noteId, trackingFrom }) => {
    const classes = useStyles();

    const tracker = useInstance(CareMapNoteTracker);
    const editNoteDrawer = useInstance(EditNoteWithQueryParamDrawer);
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);
    const appointmentsState = useInstance(AppointmentsState);
    const appointmentEditorDrawer = useInstance(AppointmentEditorDrawer);

    const notesFactory = useInstance(CaremapNotesModelFactory);
    useSubscription(() => notesFactory);

    const model = notesFactory.forActiveCaremap();
    const note = model.getNote(noteId);

    // Get appointment if linked through recording
    const appointmentId = note?.otherAttributes?.recording?.appointmentId;
    const appointment = appointmentId ? appointmentsState.getAppointment(appointmentId) : undefined;

    useEffect(() => {
        if (note) {
            tracker.onNoteViewed(note, trackingFrom);
        }
    }, [noteId]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!note) {
        return;
    }

    const canEdit = model.canEdit(note);

    const editHandler = () => {
        editNoteDrawer.open({ noteId: note.noteId, trackingFrom });
    };

    const handleAppointmentClick = () => {
        if (appointment) {
            appointmentEditorDrawer.open({
                appointmentId: appointment.appointmentId,
                trackingFrom: 'care recipient'
            });
        }
    };

    return (
        <div data-type="view-note" data-note-id={note.noteId} className={classes.stretchHeight}>

            <div style={{display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between'}}>
                <div style={{fontSize: 20, fontWeight: 'bold'}}>{note.title}</div>
                <ViewNoteMenu model={model} noteId={noteId} trackingFrom={trackingFrom} onCloseDrawer={drawerHelper.close} />
            </div>

            <NoteDateTarget note={note} showNoteTargetHeader showBothDates />

            {note.otherAttributes?.recording?.summaryPrompt !== 'intro' && (
                <UserNotes
                    content={note.html}
                    editHandler={canEdit ? editHandler : undefined}
                    hasAISummary={!!note.otherAttributes?.recording?.summary?.detailedSummary}
                />
            )}

            {appointment && (
                <AppointmentLink
                    appointment={appointment}
                    onClick={handleAppointmentClick}
                />
            )}

            {note.otherAttributes?.recording?.summaryPrompt === 'intro' ? (
                <BeaCommentBubble note={note} />
            ) : (
                <ShareNoteNudge note={note} />
            )}

            {note.otherAttributes?.recording?.summaryPrompt === 'intro' && (
                <UserNotes
                    content={note.html}
                    editHandler={canEdit ? editHandler : undefined}
                    hasAISummary={!!note.otherAttributes?.recording?.summary?.detailedSummary}
                />
            )}

            <Overview note={note} />

            {note.otherAttributes?.recording?.summaryPrompt !== 'intro' && (
                <Participants note={note} />
            )}

            <DetailedSummary note={note} />

            <SmartTasks note={note} />

            <SummaryFeedback note={note} />

            <Attachments
                note={note}
                editHandler={canEdit ? editHandler : undefined}
            />

            <TagsSection note={note} />

            <Transcription note={note} />
        </div>
    );
};

export const ViewNoteDrawerView: FC<IViewNoteDrawerProps> = ({ noteId }) => {
    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);
    return (
        <QueryParamDrivenDrawerPage
            title="View Note"
            hasBack={false}
            data-type="view-note-drawer"
            onClose={drawerHelper.close}
        >
            <BlockUntilActiveCaremap>
                <ViewNoteDrawerContent
                    noteId={noteId}
                    trackingFrom={drawerHelper.getDrawerParams().trackingFrom}
                />
            </BlockUntilActiveCaremap>
        </QueryParamDrivenDrawerPage>
    );
};
